import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import {
  IndianRupee, TriangleAlert, Package, Siren, Download, ChevronRight, X, BarChart3,
  ClipboardCheck, ClipboardList, PackageCheck, Truck, Ban, Trophy, Boxes, Wallet, Activity,
  FileText, Users, Rocket, ShoppingCart, Wrench, Receipt, MapPin, Landmark, Building2, Megaphone, Tag,
  Bell, LogIn, KeyRound,
} from 'lucide-react';
import { request, api, fileUrl } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, useAuthStore } from '../../store/auth';
import { brand } from '../../assets/brand';
import { Select, Dropdown, Button, useApp, Alert, Tooltip, DateRangePicker, Segmented, cn } from '../../ui';
import { Delta, Empty, Kpi, Panel, PanelLink } from './dashboardParts';
import { rupee } from '../../lib/format';
import { territoryOption } from '../../lib/territoryLabel';

const inr = (n: any) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n ?? 0));

/** ₹1.55 Cr / ₹6.52 L — headline figures, where the exact rupee is noise. */
const inrShort = (n: any): string => {
  const v = Number(n ?? 0);
  const a = Math.abs(v);
  if (a >= 1_00_00_000) return rupee(v, `${(a / 1_00_00_000).toFixed(2)} Cr`);
  if (a >= 1_00_000) return rupee(v, `${(a / 1_00_000).toFixed(2)} L`);
  return inr(v);
};

const compact = (v: any): string => {
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1_00_00_000) return `${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (a >= 1_00_000) return `${(n / 1_00_000).toFixed(1)}L`;
  if (a >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
};

const num = (n: any) => Number(n ?? 0).toLocaleString('en-IN');

type RankTab = 'products' | 'people' | 'territories';

/** Percentage change, or null when there is nothing honest to compare against. */
const pctChange = (now: number, before: number): number | null => {
  if (!Number.isFinite(now) || !Number.isFinite(before)) return null;
  if (before === 0) return now === 0 ? 0 : null; // growth from zero has no percentage
  return ((now - before) / Math.abs(before)) * 100;
};

/** "2h ago" — relative time without pulling in a dayjs plugin the app may not register. */
const ago = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return dayjs(iso).format('DD MMM');
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 7 ? `${d}d ago` : dayjs(iso).format('DD MMM');
};

function DownloadReportButton() {
  const { message } = useApp();
  const [busy, setBusy] = useState(false);
  const download = async (period: string, from: string, to: string) => {
    setBusy(true);
    try {
      const res = await api.get('/reports/summary.pdf', { responseType: 'blob', params: { from, to, period } });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `GBB-report-${from}_${to}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not generate the report.');
    } finally {
      setBusy(false);
    }
  };
  const onClick = ({ key }: { key: string }) => {
    const today = dayjs().format('YYYY-MM-DD');
    if (key === 'today') download('daily', today, today);
    else if (key === 'month') download('monthly', dayjs().startOf('month').format('YYYY-MM-DD'), today);
    else if (key === 'year') download('yearly', dayjs().startOf('year').format('YYYY-MM-DD'), today);
  };
  return (
    <Dropdown
      disabled={busy}
      menu={{ onClick, items: [
        { key: 'today', label: 'Daily — today' },
        { key: 'month', label: 'Monthly — this month' },
        { key: 'year', label: 'Yearly — this year' },
      ] }}
    >
      <Button size="sm" loading={busy} icon={<Download size={15} />}>Report</Button>
    </Dropdown>
  );
}

const PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: 'Today', value: [dayjs(), dayjs()] },
  { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), dayjs()] },
  { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'This month', value: [dayjs().startOf('month'), dayjs()] },
  { label: 'Last quarter', value: [dayjs().subtract(90, 'day'), dayjs()] },
];

/**
 * Order STAGES — a rollup of raw statuses into one bucket.
 *
 * `statuses` is not documentation, it is the DESTINATION: a stage row links to
 * `/orders?status=<its statuses, comma-joined>`, which the orders list accepts, so pressing
 * "Packed · 14" opens exactly those fourteen.
 */
const STAGES: { key: string; label: string; icon: ReactNode; statuses: string[]; danger?: boolean }[] = [
  { key: 'awaiting', label: 'Awaiting approval', icon: <ClipboardCheck className="size-4" />, statuses: ['DRAFT', 'PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED'] },
  { key: 'processing', label: 'Processing', icon: <ClipboardList className="size-4" />, statuses: ['APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'AWAITING_PRODUCTION', 'IN_PRODUCTION'] },
  { key: 'packed', label: 'Packed', icon: <Package className="size-4" />, statuses: ['PACKED'] },
  { key: 'transit', label: 'In transit', icon: <Truck className="size-4" />, statuses: ['DISPATCHED'] },
  { key: 'delivered', label: 'Delivered', icon: <PackageCheck className="size-4" />, statuses: ['DELIVERED', 'INVOICED', 'CLOSED'] },
  // A REJECTED order is as dead as a cancelled one — it never waits for anything again — so it sits
  // here, never under "Awaiting", and the label names both so the count is not read as cancellations only.
  { key: 'cancelled', label: 'Cancelled / rejected', icon: <Ban className="size-4" />, statuses: ['CANCELLED', 'REJECTED'], danger: true },
];

/** Statuses that are finished without becoming business — excluded from every "active" count. */
const DEAD_STATUSES = new Set(['CANCELLED', 'REJECTED', 'DRAFT']);

/**
 * Money COLLECTED in an inclusive `YYYY-MM-DD` window — the one definition the dashboard shares with
 * the mobile app: payments DATED inside the window that were APPROVED, are RECEIVED or RECONCILED
 * (a bounced or refunded payment brought nothing in), and were not settled from the dealer's own
 * account credit (no new money arrived). `MONEY_IN` narrows the server list to approved, non-credit
 * rows; the status rule is applied here. The list is capped server-side, so `capped` says when the
 * sum may be short.
 */
const PAYMENT_LIST_CAP = 300;
function collectedInWindow(rows: any[]): { total: number; count: number; capped: boolean } {
  let total = 0, count = 0;
  for (const p of rows ?? []) {
    const st = String(p.status ?? '').toUpperCase();
    if (String(p.approvalStatus ?? '').toUpperCase() !== 'APPROVED') continue;
    if (p.fromCredit === true || p.fromCredit === 1) continue;
    if (st !== 'RECEIVED' && st !== 'RECONCILED') continue;
    total += Number(p.amount || 0);
    count++;
  }
  return { total, count, capped: (rows?.length ?? 0) >= PAYMENT_LIST_CAP };
}

/** Ageing severity — the validated 4-step STATUS palette (good → critical). Always label-led. */
const AGING = [
  { key: 'b0_30', name: '0 – 30 days', fill: '#0ca30c' },
  { key: 'b31_60', name: '31 – 60 days', fill: '#fab219' },
  { key: 'b61_90', name: '61 – 90 days', fill: '#ec835a' },
  { key: 'b_gt90', name: '90+ days', fill: '#d03b3b' },
] as const;

/**
 * The podium. Gold, silver, bronze, then plain.
 *
 * A ranked list where every rank chip looks identical wastes the one place colour is genuinely
 * meaningful: the top three ARE different, and saying so with a medal is instantly readable in a way
 * that "1, 2, 3" in matching grey boxes is not.
 */
const RANK_MEDAL = [
  'bg-harvest-100 text-harvest-800 ring-1 ring-harvest-400/50 dark:bg-harvest-400/20 dark:text-harvest-200',
  'bg-surface-3 text-content ring-1 ring-line-strong',
  'bg-[#f0dcc8] text-[#8a5a2b] ring-1 ring-[#c99a6b]/50 dark:bg-[#7a5232]/30 dark:text-[#e0b489]',
];

/**
 * A stable identity tint per row.
 *
 * Picked from the NAME, not the row index, so a product keeps its colour when the ranking reorders
 * or the period changes — colour follows the entity, never its rank. (Recolour-on-filter is the
 * classic dashboard mistake: a reader who learned "Paracetamol is violet" is misled the moment a
 * filter moves it up a place.)
 */
const IDENTITY_TINTS = [
  'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300',
  'bg-sky-400/15 text-sky-600 dark:bg-sky-400/20 dark:text-sky-300',
  'bg-plum-400/15 text-plum-600 dark:bg-plum-400/20 dark:text-plum-300',
  'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/20 dark:text-harvest-300',
  'bg-danger-500/10 text-danger-600 dark:bg-danger-500/20 dark:text-danger-400',
];
function identityTint(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return IDENTITY_TINTS[h % IDENTITY_TINTS.length];
}

/** Two letters from a name, for the avatar when there is no photograph. */
function initials(name: string): string {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

/*
 * This panel is a WINDOW ONTO /admin/activity, not a curated digest of it. It asks for the same
 * feed, in the same order, with no module filter of any kind — so its rows are the newest six
 * rows of the page its "View all" button opens, and the two can never disagree.
 *
 * It has been filtered twice before and both times the panel started lying. An allow-list of
 * eight modules aged into an order log: two of the eight (STOCK, EXPENSE) are not names the app
 * writes at all, and everything that happens to an order after it is raised had since moved to
 * SHIPMENT / ORDER_APPROVAL / GST_DETERMINATION / CREDIT_NOTE, none of which were listed. Even a
 * deny-list of sign-in and notification chatter left a reader comparing two screens that showed
 * different things. Whatever the activity page shows, this shows. Do not add a filter here.
 */

/**
 * Where an activity row goes when pressed.
 *
 * Every one of these lines names a real document — "Payment PAY/2026/000381 deleted",
 * "GB/2026/09/00179 packed" — and a feed that tells you something happened and then strands you
 * is half a feature. `referenceId` is the record the entry was written against: deep-link it
 * where the module HAS a detail page AND the reference really is that page's id, otherwise land
 * on the module's register, which is still closer than the reader was.
 *
 * The distinction matters. A SHIPMENT row's reference is the shipment's own id, not its order's,
 * so /orders/<shipmentId> would open a DIFFERENT, unrelated order. For rows like that the server
 * names the record to open instead (`linkModule` + `linkId`) and it wins; where it says nothing,
 * these two tables decide. Rows in neither are not buttons at all.
 */

/** Modules whose referenceId IS the id of a page that exists. */
const ACTIVITY_DETAIL: Record<string, (id: string) => string> = {
  ORDER: (id) => `/orders/${id}`,
  ORDER_APPROVAL: (id) => `/orders/${id}`,
  APPROVAL: (id) => `/orders/${id}`,
  GST_DETERMINATION: (id) => `/orders/${id}`,
  INVOICE: (id) => `/billing/invoices/${id}`,
  RETURN: (id) => `/returns/${id}`,
  COMPLAINT: (id) => `/complaints/${id}`,
  EVENT: (id) => `/events/${id}`,
  EXPENSE_REPORT: (id) => `/expense-reports/${id}`,
  RETAILER: (id) => `/retailers/${id}`,
  USER: (id) => `/staff/${id}`,
};

/** Everything else: the register the row belongs to. */
const ACTIVITY_REGISTER: Record<string, string> = {
  ORDER: '/orders', ORDER_APPROVAL: '/orders/approval', APPROVAL: '/approvals', SHIPMENT: '/warehouse/dispatches',
  GST_DETERMINATION: '/orders', INQUIRY: '/inquiries',
  INVOICE: '/billing/invoices', PAYMENT: '/billing/payments', BANK_RECONCILIATION: '/billing/payments',
  CREDIT_NOTE: '/billing/credit-notes', RETURN: '/returns',
  INVENTORY: '/inventory/adjustments', STOCK_TRANSFER: '/inventory/transfers',
  WAREHOUSE: '/masters/warehouses', TRANSPORTER: '/warehouse/transporters',
  PRODUCT: '/products', PRODUCT_DETAILS: '/products', SCHEME: '/masters/schemes',
  COMMERCIAL_SCHEME: '/masters/commercial-terms', TAX: '/masters/tax', GST_REGISTRATION: '/masters/gst-registrations',
  COMPLAINT: '/complaints', COMPLAINT_THREAD: '/complaints', EVENT: '/events',
  VISIT: '/ffa/visits', ROUTE: '/ffa/visits', FFA: '/ffa/attendance', FEEDBACK: '/ffa/feedback',
  EXPENSE: '/expense-reports', EXPENSE_REPORT: '/expense-reports', ADVANCE_REQUEST: '/expense-reports',
  WALLET: '/wallets', COMPANY_BANK: '/admin/bank-details',
  USER: '/people', RETAILER: '/retailers', FARMER: '/farmers', ROLE: '/admin/roles',
  TERRITORY: '/masters/territories', TERRITORY_ASSIGNMENT: '/masters/territory-coverage',
  NOTIFICATION_TEMPLATE: '/notifications/templates', WA_TEMPLATE: '/notifications/wa-templates',
  BANNER: '/admin/banners', PROFILE: '/profile',
};

function activityHref(a: {
  module?: string; referenceId?: number | string | null;
  linkModule?: string | null; linkId?: number | string | null;
}): string | null {
  const mod = (a.linkId != null ? a.linkModule ?? a.module : a.module) ?? '';
  const ref = a.linkId != null ? a.linkId : a.referenceId;
  const id = ref != null && String(ref).length ? String(ref) : null;
  const detail = id ? ACTIVITY_DETAIL[mod] : undefined;
  if (id && detail) return detail(id);
  return ACTIVITY_REGISTER[a.module ?? ''] ?? null;
}

/* Lane colours, the same ones the KPI row and the quick-action tiles use: sales green, money
   sky, billing amber, inventory violet, field ops red. A module the map has not met yet still
   gets a neutral dot rather than nothing. */
const A_SALES = 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300';
const A_MONEY = 'bg-sky-400/12 text-sky-600 dark:text-sky-400';
const A_BILL = 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300';
const A_STOCK = 'bg-plum-400/12 text-plum-600 dark:text-plum-400';
const A_FIELD = 'bg-danger-500/10 text-danger-600 dark:text-danger-500';
const A_QUIET = 'bg-surface-2 text-muted';

const ACTIVITY_ICON: Record<string, { icon: ReactNode; tone: string }> = {
  ORDER: { icon: <ShoppingCart />, tone: A_SALES },
  ORDER_APPROVAL: { icon: <ClipboardCheck />, tone: A_SALES },
  APPROVAL: { icon: <ClipboardCheck />, tone: A_SALES },
  SHIPMENT: { icon: <Truck />, tone: A_SALES },
  INQUIRY: { icon: <ClipboardList />, tone: A_SALES },
  INVOICE: { icon: <Receipt />, tone: A_BILL },
  CREDIT_NOTE: { icon: <Receipt />, tone: A_BILL },
  GST_DETERMINATION: { icon: <FileText />, tone: A_BILL },
  GST_REGISTRATION: { icon: <FileText />, tone: A_BILL },
  TAX: { icon: <FileText />, tone: A_BILL },
  PAYMENT: { icon: <IndianRupee />, tone: A_MONEY },
  BANK_RECONCILIATION: { icon: <Landmark />, tone: A_MONEY },
  COMPANY_BANK: { icon: <Landmark />, tone: A_MONEY },
  WALLET: { icon: <Wallet />, tone: A_MONEY },
  EXPENSE: { icon: <Wallet />, tone: A_MONEY },
  EXPENSE_REPORT: { icon: <Wallet />, tone: A_MONEY },
  ADVANCE_REQUEST: { icon: <Wallet />, tone: A_MONEY },
  RETURN: { icon: <Package />, tone: A_STOCK },
  INVENTORY: { icon: <Boxes />, tone: A_STOCK },
  STOCK_TRANSFER: { icon: <Truck />, tone: A_STOCK },
  WAREHOUSE: { icon: <Building2 />, tone: A_STOCK },
  TRANSPORTER: { icon: <Truck />, tone: A_STOCK },
  PRODUCT: { icon: <Package />, tone: A_STOCK },
  PRODUCT_DETAILS: { icon: <Package />, tone: A_STOCK },
  SCHEME: { icon: <Tag />, tone: A_STOCK },
  COMMERCIAL_SCHEME: { icon: <Tag />, tone: A_STOCK },
  COMPLAINT: { icon: <Wrench />, tone: A_FIELD },
  COMPLAINT_THREAD: { icon: <Wrench />, tone: A_FIELD },
  VISIT: { icon: <MapPin />, tone: A_FIELD },
  ROUTE: { icon: <MapPin />, tone: A_FIELD },
  FFA: { icon: <MapPin />, tone: A_FIELD },
  EVENT: { icon: <MapPin />, tone: A_FIELD },
  FEEDBACK: { icon: <MapPin />, tone: A_FIELD },
  TERRITORY: { icon: <MapPin />, tone: A_QUIET },
  TERRITORY_ASSIGNMENT: { icon: <MapPin />, tone: A_QUIET },
  USER: { icon: <Users />, tone: A_QUIET },
  ROLE: { icon: <Users />, tone: A_QUIET },
  RETAILER: { icon: <Users />, tone: A_QUIET },
  FARMER: { icon: <Users />, tone: A_QUIET },
  BANNER: { icon: <Megaphone />, tone: A_QUIET },
  CONTENT: { icon: <Megaphone />, tone: A_QUIET },
  NOTIFICATION_TEMPLATE: { icon: <Megaphone />, tone: A_QUIET },
  WA_TEMPLATE: { icon: <Megaphone />, tone: A_QUIET },
  NOTIFICATION: { icon: <Bell />, tone: A_QUIET },
  NOTIFICATION_PREFS: { icon: <Bell />, tone: A_QUIET },
  AUTH: { icon: <LogIn />, tone: A_QUIET },
  PASSWORD: { icon: <KeyRound />, tone: A_QUIET },
  PROFILE: { icon: <Users />, tone: A_QUIET },
  IMPORT: { icon: <FileText />, tone: A_QUIET },
  UPLOAD: { icon: <FileText />, tone: A_QUIET },
  REPORT: { icon: <FileText />, tone: A_QUIET },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SeriesTooltip({ active, payload, label, money, name }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop">
      <div className="font-semibold text-content">{dayjs(label).format('DD MMM YYYY')}</div>
      <div className="text-muted">{name}: <span className="font-medium text-content">{money ? inr(payload[0].value) : num(payload[0].value)}</span></div>
    </div>
  );
}

export function ExecSalesDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [territoryId, setTerritoryId] = useState<string | undefined>();
  const [salesPersonId, setSalesPersonId] = useState<string | undefined>();
  const [metricPref, setMetricPref] = useState<'revenue' | 'orders' | null>(null);
  const [rankTab, setRankTab] = useState<RankTab>('products');
  const [alertOpen, setAlertOpen] = useState(true);

  const canExec = usePermission('DASH.EXECUTIVE');
  const canOutstanding = usePermission('OUTSTANDING.VIEW');
  const canSales = usePermission('DASH.SALES');
  const canViewUsers = usePermission('USER.VIEW');
  const canActivity = usePermission('ACTIVITY.VIEW');
  const canPayments = usePermission('PAYMENT.VIEW');
  const me = useAuthStore((st) => st.user);

  const roleView: 'company' | 'team' | 'individual' = canExec ? 'company' : canOutstanding ? 'team' : 'individual';
  const title = roleView === 'company' ? 'Company Performance' : roleView === 'team' ? "Your Team's Performance" : 'Your Performance';

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');
  const params = { from, to, salesPersonId, territoryId };

  /**
   * The SAME window, immediately before this one — the basis for every "vs last period" on the page.
   *
   * A KPI without a comparison is a number without a verdict: ₹6.5 lakh collected is good news or
   * bad news depending entirely on what the previous month did, and the reader cannot know which. It
   * is the same endpoint with shifted dates, so the comparison is like-for-like by construction.
   */
  const days = Math.max(1, range[1].diff(range[0], 'day') + 1);
  /** The period in words, for a tile that must say which dates it counted ("05 Oct – 03 Nov 2026"). */
  const periodCaption = range[0].isSame(range[1], 'day')
    ? range[0].format('DD MMM YYYY')
    : `${range[0].format(range[0].isSame(range[1], 'year') ? 'DD MMM' : 'DD MMM YYYY')} – ${range[1].format('DD MMM YYYY')}`;
  const prevFrom = range[0].subtract(days, 'day').format('YYYY-MM-DD');
  const prevTo = range[0].subtract(1, 'day').format('YYYY-MM-DD');

  const execQ = useQuery({
    queryKey: ['dashboard-executive'],
    queryFn: () => request<any>({ url: '/dashboard/executive' }),
    enabled: canExec,
  });
  const agingQ = useQuery({
    queryKey: ['dashboard-aging'],
    queryFn: () => request<any[]>({ url: '/outstanding/aging' }),
    enabled: canOutstanding,
  });
  const summaryQ = useQuery({
    queryKey: ['dashboard-sales-summary', params],
    queryFn: () => request<any>({ url: '/dashboard/sales/summary', params }),
    enabled: canSales,
  });
  const prevQ = useQuery({
    queryKey: ['dashboard-sales-summary-prev', prevFrom, prevTo, salesPersonId, territoryId],
    queryFn: () => request<any>({ url: '/dashboard/sales/summary', params: { from: prevFrom, to: prevTo, salesPersonId, territoryId } }),
    enabled: canSales,
  });
  /** Collections for the PICKED period (not month-to-date), so the tile moves with the date range. */
  const collectedQ = useQuery({
    queryKey: ['dashboard-collected', from, to],
    queryFn: () => request<any[]>({ url: '/payments', params: { from, to, status: 'MONEY_IN' } }),
    enabled: canExec && canPayments,
  });
  const collected = useMemo(() => collectedInWindow(collectedQ.data ?? []), [collectedQ.data]);
  const topQ = useQuery({
    queryKey: ['dashboard-top-skus', from, to],
    queryFn: () => request<any[]>({ url: '/dashboard/products/top', params: { from, to, limit: 5 } }),
    enabled: canSales,
  });
  const territoriesQ = useQuery({
    queryKey: ['territories-list'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: canSales,
  });
  const usersQ = useQuery({
    queryKey: ['sales-users'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: canSales && canViewUsers,
  });
  /**
   * Product photographs for the leaderboard.
   *
   * `/dashboard/products/top` returns ids, names and figures — no image — and rather than widen that
   * query we join to the product catalogue the client can already read. The ranking is five rows, so
   * this costs one cached request and keeps the analytics endpoint about analytics.
   *
   * `enabled` on the products tab only: nobody needs the catalogue to look at a rep leaderboard.
   */
  const productImgQ = useQuery({
    queryKey: ['dashboard-product-images'],
    queryFn: () => requestAllPages('/products'),
    enabled: canSales && rankTab === 'products',
    staleTime: 5 * 60_000,
  });
  const productImg = useMemo(() => {
    const m = new Map<string, string>();
    for (const pr of productImgQ.data?.data ?? []) {
      const src = pr.imageUrl || pr.imageUrls?.[0];
      if (src) m.set(String(pr.id), src);
    }
    return m;
  }, [productImgQ.data]);

  const activityQ = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => request<any>({ url: '/activity-logs', params: { page: 1, limit: 6, sort: 'desc' } }),
    enabled: canActivity,
  });

  const exec = execQ.data ?? {};
  const summary = summaryQ.data ?? {};
  const prev = prevQ.data ?? {};

  const aging = useMemo(() => (agingQ.data ?? []).reduce(
    (a: any, r: any) => ({
      b0_30: a.b0_30 + Number(r.b0_30 || 0),
      b31_60: a.b31_60 + Number(r.b31_60 || 0),
      b61_90: a.b61_90 + Number(r.b61_90 || 0),
      b_gt90: a.b_gt90 + Number(r.b_gt90 || 0),
      total: a.total + Number(r.total || 0),
    }),
    { b0_30: 0, b31_60: 0, b61_90: 0, b_gt90: 0, total: 0 },
  ), [agingQ.data]);
  const overdue30 = aging.b31_60 + aging.b61_90 + aging.b_gt90;

  /** Daily series, backfilled to zero for every day in range, carrying BOTH measures. */
  const trend = useMemo(() => {
    const money = new Map<string, number>();
    const count = new Map<string, number>();
    for (const t of summary.trend ?? []) {
      const k = String(t.day).slice(0, 10);
      money.set(k, Number(t.total || 0));
      count.set(k, Number(t.orders || 0));
    }
    const out: { day: string; total: number; orders: number }[] = [];
    let cur = range[0].startOf('day');
    const end = range[1].startOf('day');
    let guard = 0;
    while (!cur.isAfter(end) && guard < 400) {
      const k = cur.format('YYYY-MM-DD');
      out.push({ day: k, total: money.get(k) ?? 0, orders: count.get(k) ?? 0 });
      cur = cur.add(1, 'day');
      guard++;
    }
    return out;
  }, [summary.trend, range]);

  const hasRevenue = trend.some((d) => d.total > 0);
  const hasOrders = trend.some((d) => d.orders > 0);
  const metric: 'revenue' | 'orders' = metricPref ?? (hasRevenue ? 'revenue' : 'orders');
  const orderSpark = trend.map((d) => d.orders);
  const revenueSpark = trend.map((d) => d.total);

  const byPerson = summary.byPerson ?? [];
  // Memoised so the `?? []` fallback is one stable array — a fresh one per render re-ran every memo below.
  const byStatus = useMemo(() => summary.byStatus ?? [], [summary.byStatus]);
  const topSkus = topQ.data ?? [];
  const activity = activityQ.data?.activity_logs ?? [];

  // `created` counts EVERY order incl. cancelled/draft; `live` counts neither. Different questions.
  const created = useMemo(() => byStatus.reduce((s: number, x: any) => s + Number(x.count || 0), 0), [byStatus]);
  const prevCreated = useMemo(() => (prev.byStatus ?? []).reduce((s: number, x: any) => s + Number(x.count || 0), 0), [prev.byStatus]);
  // ACTIVE = everything in the status mix except the dead ends. The server's `orderCount` keeps
  // REJECTED (it only drops CANCELLED and DRAFT), so it is not used here: a rejected order is not live.
  const activeOf = (rows: any[]) => (rows ?? []).reduce(
    (s: number, x: any) => s + (DEAD_STATUSES.has(String(x.status)) ? 0 : Number(x.count || 0)), 0);
  const live = useMemo(() => activeOf(byStatus), [byStatus]);
  const prevLive = useMemo(() => activeOf(prev.byStatus ?? []), [prev.byStatus]);
  const billed = Number(summary.revenueOrders ?? 0);
  const revenue = Number(summary.salesTotal ?? 0);

  const stages = useMemo(() => {
    const countBy: Record<string, number> = {};
    const valueBy: Record<string, number> = {};
    for (const s of byStatus) {
      countBy[s.status] = Number(s.count || 0);
      valueBy[s.status] = Number(s.total || 0);
    }
    return STAGES.map((st) => {
      const inside = st.statuses.filter((s) => countBy[s] > 0).map((s) => ({ status: s, count: countBy[s] }));
      const count = inside.reduce((a, b) => a + b.count, 0);
      const value = st.statuses.reduce((a, s) => a + (valueBy[s] ?? 0), 0);
      return { ...st, count, value, inside, pct: created ? (count / created) * 100 : 0 };
    });
  }, [byStatus, created]);

  const cancelled = stages.find((s) => s.key === 'cancelled');
  const cancelPct = created ? Math.round(((cancelled?.count ?? 0) / created) * 100) : 0;

  const go = (url: string) => navigate(url);
  const periodQs = (extra: Record<string, string | undefined> = {}) =>
    Object.entries({ from, to, ...extra })
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');

  // The WHOLE name. Trimming to the first word turned "Super Admin" into "Super", which is not
  // what anyone is called — a greeting that gets the reader's name wrong is worse than no greeting.
  const myName = String(me?.fullName ?? '').trim() || 'there';
  const greeting = (() => {
    const h = dayjs().hour();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  })();

  /**
   * The ranking table's rows.
   *
   * Territories are ROLLED UP from the same per-person figures the leaderboard already uses rather
   * than fetched: a rep belongs to a territory, so summing their billed revenue by territory adds a
   * third view without a third query - and without a second definition of "revenue" that could
   * drift from the first.
   */
  const rankRows: { name: string; sub: string; qty: number; value: number; link?: string; img?: string }[] = useMemo(() => {
    if (rankTab === 'products') {
      return topSkus.map((p: any) => ({
        name: p.name || p.sku,
        sub: p.name && p.sku && p.name !== p.sku ? p.sku : '',
        qty: Number(p.qty ?? 0),
        value: Number(p.revenue ?? 0),
        link: p.sku ? '/products?sku=' + encodeURIComponent(p.sku) : undefined,
        img: productImg.get(String(p.product_id)),
      }));
    }
    if (rankTab === 'people') {
      return byPerson.slice(0, 5).map((p: any) => ({
        name: p.fullName,
        sub: '',
        qty: Number(p.orders ?? 0),
        value: Number(p.total ?? 0),
        link: p.salesPersonId ? '/orders?salesPersonId=' + p.salesPersonId + '&' + periodQs() : undefined,
      }));
    }
    const byTerr = new Map<string, { qty: number; value: number }>();
    for (const p of byPerson) {
      const key = p.territoryName || p.territoryCode || 'Unassigned';
      const cur = byTerr.get(key) ?? { qty: 0, value: 0 };
      byTerr.set(key, { qty: cur.qty + Number(p.orders ?? 0), value: cur.value + Number(p.total ?? 0) });
    }
    return [...byTerr.entries()]
      .map(([name, v]) => ({ name, sub: '', qty: v.qty, value: v.value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankTab, topSkus, byPerson, productImg, from, to, salesPersonId]);
  // The SHARE column's denominator: the total of the rows on show, so a bar's length always
  // means "this much of what you are looking at" rather than "this much of row one".
  const rankTotal = Math.max(1, rankRows.reduce((sum, r) => sum + r.value, 0));

  return (
    <div className="space-y-4">
      {/* ── Header ───────────────────────────────────────────────────────────────────────────────
          The greeting, and every control that narrows the page.

          There WAS a landscape illustration bleeding off the right edge. It is gone, and removing it
          is the right answer rather than a third attempt at drawing it: an illustration needs
          vertical room to read as a picture, this band is barely 100px tall, and at that height it
          kept arriving as a pale green smear behind the filters — decoration that pulls the eye
          without rewarding it. A header whose job is a greeting and five controls is finished
          without a picture.

          No `overflow-hidden` either: it used to clip the date picker's calendar, which opened and
          was sliced off a few pixels below its own month header. */}
      {/* The band is washed brand-green from the left into harvest gold at the right — the two brand
          hues, at 7-8%, laid diagonally so the greeting end reads warmest. This is the one place on
          the app where a decorative wash earns its keep: it is the first surface the reader meets
          after logging in, and the greeting it carries has no data of its own to colour. */}
      <header className="rounded-card border border-line bg-surface bg-linear-to-br from-brand-500/[0.10] via-transparent via-55% to-harvest-400/[0.12] shadow-e1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 py-4">
          <div className="min-w-0">
            {/* The reader's own name, in the brand colour. A dashboard is a personal screen; greeting
                the person who opened it costs one word and is the difference between a report and a
                workspace. */}
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-content sm:text-[28px]">
              {greeting}, <span className="text-brand-600 dark:text-brand-400">{myName}</span> 👋
            </h1>
            <p className="mt-1 text-sm text-muted">
              {title} · here’s what’s happening with your business
              {exec.refreshedAt ? ` · updated ${dayjs(exec.refreshedAt).format('hh:mm A')}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSales && <DateRangePicker value={range} onChange={setRange} presets={PRESETS} />}
            {canSales && (
              <Select
                placeholder="All territories" allowClear showSearch optionFilterProp="label" style={{ width: 170 }}
                value={territoryId} onChange={setTerritoryId}
                options={(territoriesQ.data ?? []).map((t: any) => ({ value: String(t.id), ...territoryOption(t) }))}
              />
            )}
            {canSales && canViewUsers && (
              <Select
                placeholder="All sales persons" allowClear showSearch optionFilterProp="label" style={{ width: 180 }}
                value={salesPersonId} onChange={setSalesPersonId}
                options={(usersQ.data?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))}
              />
            )}
            {canExec && <DownloadReportButton />}
          </div>
        </div>
      </header>

      {(execQ.isError || summaryQ.isError || agingQ.isError) && (
        <Alert type="error" icon={<TriangleAlert />} title="Some dashboard data couldn't be loaded"
          description="Figures below may be incomplete — please retry shortly." />
      )}

      {/* ── The finding ──────────────────────────────────────────────────────────────────────────
          Shown only when it is true. A banner that is always there is furniture; one that appears
          because something crossed a line is information. Dismissable, because a reader who has seen
          it and decided what to do should not keep scrolling past it. */}
      {canSales && alertOpen && cancelPct >= 25 && (cancelled?.count ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-danger-500/25 bg-danger-500/6 px-4 py-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-danger-500/15 text-danger-600 dark:text-danger-500" aria-hidden>
            <TriangleAlert className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-content">
              {cancelPct}% of orders were cancelled or rejected — {num(cancelled!.count)} of {num(created)}
            </p>
            <p className="text-xs text-muted">{inrShort(cancelled!.value)} of order value did not go through. Worth checking why.</p>
          </div>
          <Button size="sm" variant="soft-danger"
            onClick={() => go(`/orders?${periodQs({ status: cancelled!.statuses.join(','), salesPersonId })}`)}>
            See details
          </Button>
          <button type="button" aria-label="Dismiss" onClick={() => setAlertOpen(false)}
            className="rounded-control p-1 text-subtle transition-colors hover:bg-danger-500/10 hover:text-danger-600">
            <X className="size-4" />
          </button>
        </div>
      )}

      {/* ── KPI row ──────────────────────────────────────────────────────────────────────────────
          Eight figures, one shape, every one of them a link. The four PERIOD figures carry a real
          movement against the same window immediately before, plus a sparkline drawn from the daily
          series. The four LIVE figures carry neither — no series exists for them, and a decorative
          squiggle next to a real one would make both untrustworthy. */}
      {/* Eight columns only from 2xl. At xl the eight tracks were 142px wide — NARROWER than the four
          tracks the same page gives a 1024px screen — so a wider browser clipped more, not less. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-8">
        {canSales && (
          <>
            <Kpi
              icon={<FileText />} tone="brand" label="Orders Created" value={num(created)} loading={summaryQ.isLoading}
              spark={orderSpark} delta={<Delta pct={pctChange(created, prevCreated)} />}
              onClick={() => go(`/orders?${periodQs({ salesPersonId })}`)}
            />
            <Kpi
              // Counted from the status mix, so REJECTED is out along with CANCELLED and DRAFT — the
              // tile no longer holds orders that are definitively dead.
              icon={<Activity />} tone="sky" label="Active Orders" value={num(live)} loading={summaryQ.isLoading}
              delta={<Delta pct={pctChange(live, prevLive)} />}
              sub="excludes cancelled, rejected & draft"
              onClick={() => go(`/orders?${periodQs({ salesPersonId })}`)}
            />
            <Kpi
              icon={<Receipt />} tone="violet" label="Billed" value={num(billed)} loading={summaryQ.isLoading}
              delta={<Delta pct={pctChange(billed, Number(prev.revenueOrders ?? 0))} />}
              sub="invoiced or closed"
              onClick={() => go(`/orders?${periodQs({ status: 'INVOICED,CLOSED', salesPersonId })}`)}
            />
            <Kpi
              icon={<IndianRupee />} tone="gold" label="Revenue" value={inrShort(revenue)} loading={summaryQ.isLoading}
              spark={hasRevenue ? revenueSpark : undefined}
              delta={<Delta pct={pctChange(revenue, Number(prev.salesTotal ?? 0))} />}
              sub={billed ? `${inr(summary.aov ?? 0)} average order` : 'nothing billed yet'}
              onClick={() => go(`/orders?${periodQs({ status: 'INVOICED,CLOSED', salesPersonId })}`)}
            />
          </>
        )}
        {canOutstanding && (
          <Kpi
            icon={<Wallet />} tone="gold" label="Money Owed to Us" value={inrShort(aging.total)} loading={agingQ.isLoading}
            sub={overdue30 > 0 ? `${inrShort(overdue30)} over 30 days` : 'nothing overdue'}
            onClick={() => go('/billing/aging')}
          />
        )}
        {canExec && (
          <>
            {canPayments ? (
              <Kpi
                // Follows the picked period. It used to be the month-to-date figure whatever the
                // date range said, so "Last 7 days" and "Last quarter" showed the same number.
                icon={<Boxes />} tone="sky" label="Collected" value={collectedQ.isError ? '—' : inrShort(collected.total)}
                loading={collectedQ.isLoading}
                sub={collectedQ.isError
                  ? 'could not load payments'
                  : `${periodCaption}${collected.capped ? ' · first 300 payments only' : ''}`}
                onClick={() => go(`/billing/payments?status=MONEY_IN&range=${from}~${to}`)}
              />
            ) : (
              <Kpi
                icon={<Boxes />} tone="sky" label="Collected (MTD)" value={inrShort(exec.collectionMtd)} loading={execQ.isLoading}
                sub="1st of the month to today" onClick={() => go('/billing/payments')}
              />
            )}
            <Kpi
              icon={<Package />} tone="slate" label="Stock on Hand" value={inrShort(exec.inventoryValue)} loading={execQ.isLoading}
              sub="value across all godowns" onClick={() => go('/inventory/stock')}
            />
            <Kpi
              icon={<Siren />} tone={Number(exec.complaintsSlaBreach) > 0 ? 'danger' : 'slate'}
              label="Open Complaints" value={num(exec.complaintsOpen)} loading={execQ.isLoading}
              sub={Number(exec.complaintsSlaBreach) > 0
                ? <span className="font-medium text-danger-600 dark:text-danger-500">{exec.complaintsSlaBreach} past deadline</span>
                : 'all within deadline'}
              onClick={() => go('/complaints')}
            />
          </>
        )}
      </div>

      {/* ── Trend · stages · ageing ───────────────────────────────────────────────────────────── */}
      {canSales && (
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-12">
          <Panel
            className="xl:col-span-6"
            icon={<BarChart3 />}
            title={<span className="flex flex-col"><span>Order trend</span>
              <span className="text-xs font-normal text-muted">Day by day in this period</span></span>}
            action={
              /* Two measures, one axis, one at a time — never a dual-axis plot, which would invent a
                 correlation between rupees and order counts that is not in the data. */
              <Segmented size="small" value={metric} onChange={(v) => setMetricPref(v as 'revenue' | 'orders')}
                options={[{ value: 'orders', label: 'Orders' }, { value: 'revenue', label: 'Revenue' }]} />
            }
          >
            {/* The chart draws whenever the PERIOD has anything in it — not only when the selected
                measure does. Gating on the measure meant switching to "Revenue" on a month with no
                invoices replaced the whole card with a 240px box reading "No orders in this period",
                which was both a void and untrue: there were 192 orders, they just were not billed.
                A flat line at zero is the honest picture, and the caption says why it is flat. */}
            {hasOrders || hasRevenue ? (
              <>
                {!hasRevenue && (
                  <p className="mb-2 text-xs text-muted">
                    {metric === 'revenue'
                      ? 'Nothing was billed in this period, so revenue is zero on every day.'
                      : 'Nothing was billed in this period, so revenue would be a flat line. This is what came in instead.'}
                  </p>
                )}
                <ResponsiveContainer width="100%" height={236}>
                  <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    onClick={(s: any) => s?.activeLabel && go(`/orders?from=${s.activeLabel}&to=${s.activeLabel}`)}>
                    <defs>
                      <linearGradient id="gbTrendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={brand.primary} stopOpacity={0.26} />
                        <stop offset="95%" stopColor={brand.primary} stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="var(--gb-border)" />
                    <XAxis dataKey="day" tickFormatter={(v: string) => dayjs(v).format('DD MMM')} tickLine={false} axisLine={false} minTickGap={28} tick={{ fontSize: 11 }} />
                    <YAxis
                      tickFormatter={metric === 'revenue' ? compact : (v: any) => String(v)}
                      tickLine={false} axisLine={false} width={40} tick={{ fontSize: 11 }} allowDecimals={false}
                      // An all-zero series has no range, so recharts would centre the flat line
                      // halfway up the plot as though zero were the middle of something.
                      domain={metric === 'revenue' && !hasRevenue ? [0, 1] : undefined}
                    />
                    <RTooltip cursor={{ stroke: 'var(--gb-border-strong)' }}
                      content={<SeriesTooltip money={metric === 'revenue'} name={metric === 'revenue' ? 'Billed' : 'Orders placed'} />} />
                    <Area type="monotone" dataKey={metric === 'revenue' ? 'total' : 'orders'} stroke={brand.primary}
                      strokeWidth={2} fill="url(#gbTrendFill)" dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--gb-surface)' }} cursor="pointer" />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            ) : <Empty title={summaryQ.isLoading ? 'Loading…' : 'No orders in this period'} detail="Try a wider date range." />}
          </Panel>

          <Panel
            className="xl:col-span-3" icon={<Boxes />} title="Where the orders are"
            action={<span className="text-xs text-muted">{num(created)} total</span>}
          >
            <ul className="space-y-2.5">
              {stages.map((st) => (
                <li key={st.key}>
                  <Tooltip block label={st.inside.length
                    ? `${st.inside.map((i) => `${i.status.replace(/_/g, ' ').toLowerCase()}: ${i.count}`).join(' · ')} — click to open`
                    : 'None in this period'}>
                    <button
                      type="button" disabled={st.count === 0}
                      onClick={() => go(`/orders?${periodQs({ status: st.statuses.join(','), salesPersonId })}`)}
                      className={cn('group flex w-full items-center gap-2.5 rounded-control px-1 py-1 text-left transition-colors',
                        st.count > 0 ? 'hover:bg-surface-2' : 'cursor-default opacity-50')}
                    >
                      <span className={cn('grid size-7 shrink-0 place-items-center rounded-lg',
                        st.danger ? 'bg-danger-500/10 text-danger-600 dark:text-danger-400' : 'bg-brand-50 text-brand-600 dark:bg-brand-900/25 dark:text-brand-300')}>
                        {st.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm text-content">{st.label}</span>
                          {st.value > 0 && <span className="shrink-0 text-[11px] tabular-nums text-muted">{inrShort(st.value)}</span>}
                        </span>
                        <span className="mt-1 block h-1 overflow-hidden rounded-full bg-surface-3">
                          <span className={cn('block h-full rounded-full', st.danger ? 'bg-danger-500' : 'bg-brand-500')}
                            style={{ width: `${Math.min(100, st.pct)}%` }} />
                        </span>
                      </span>
                      <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums text-content">{st.count}</span>
                    </button>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </Panel>

          {canOutstanding && (
            <Panel className="xl:col-span-3" icon={<Wallet />} title="Receivables / Ageing"
              action={<PanelLink label="Open" onClick={() => go('/billing/aging')} />}>
              {aging.total > 0 ? (
                <>
                  <p className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-content">{inr(aging.total)}</p>
                  <p className="mt-1 text-xs text-muted">Total amount owed to us</p>
                  {/* Part-to-whole: one bar, ordered by severity, 2px surface gaps between segments
                      rather than borders. Every segment is named in the legend below, so the status
                      hues never carry the meaning alone. */}
                  <div className="mt-3 flex h-3.5 w-full gap-0.5 overflow-hidden rounded-full bg-surface-3">
                    {AGING.filter((b) => aging[b.key] > 0).map((b) => (
                      <span key={b.key} className="h-full first:rounded-l-full last:rounded-r-full"
                        style={{ width: `${Math.max(2, (aging[b.key] / aging.total) * 100)}%`, background: b.fill }} aria-hidden />
                    ))}
                  </div>
                  <ul className="mt-3.5 space-y-2">
                    {AGING.map((b) => {
                      const v = aging[b.key];
                      const pct = aging.total ? Math.round((v / aging.total) * 100) : 0;
                      return (
                        <li key={b.key} className={cn('flex items-center gap-2 text-xs', v === 0 && 'opacity-45')}>
                          <span className="size-2.5 shrink-0 rounded-full" style={{ background: b.fill }} aria-hidden />
                          <span className="text-muted">{b.name}</span>
                          <span className="ml-auto font-semibold tabular-nums text-content">{inr(v)}</span>
                          <span className="w-9 shrink-0 text-right tabular-nums text-subtle">{pct}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : <Empty title="Nothing outstanding" detail="Every dealer is square with us." />}
            </Panel>
          )}
        </div>
      )}

      {/* ── Rankings · activity · actions ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-12">
        {canSales && (
          <Panel
            className="xl:col-span-5" icon={<Trophy />} title="Best sellers & leaderboard"
            action={
              <span className="flex items-center gap-2">
                {rankRows.length > 0 && (
                  <span className="hidden text-xs text-muted sm:inline">of {inrShort(rankTotal)} billed</span>
                )}
                <PanelLink label="View all" onClick={() => go(rankTab === 'products' ? '/products' : '/people')} />
              </span>
            }
          >
            <Segmented size="small" className="mb-3" value={rankTab} onChange={(v) => setRankTab(v as RankTab)}
              options={[
                { value: 'products', label: 'Products' },
                { value: 'people', label: 'Sales People' },
                { value: 'territories', label: 'Territories' },
              ]} />
            {rankRows.length ? (
              /* A TABLE, not a list of chips.
                 Ranked data has columns - a rank, a name, a volume and a value - and a reader
                 comparing five rows wants them aligned so the eye can run straight down one column.
                 Laid out as free-form rows, "42 orders . Rs 1,24,560" forces them to re-parse every
                 line. Widths are fixed so the numbers form a clean right edge.
                 It scrolls inside its own box on a narrow phone instead of pushing the page sideways. */
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="gb-thead-tint">
                  <tr className="border-b border-line text-left text-[11px] font-medium uppercase tracking-[0.06em] text-subtle">
                    <th scope="col" className="w-8 pb-2 font-medium">#</th>
                    <th scope="col" className="pb-2 font-medium">{rankTab === 'products' ? 'Product' : rankTab === 'people' ? 'Sales person' : 'Territory'}</th>
                    <th scope="col" className="hidden w-[34%] pb-2 pr-3 font-medium sm:table-cell">Share of revenue</th>
                    <th scope="col" className="w-16 pb-2 text-right font-medium">{rankTab === 'products' ? 'Units' : 'Orders'}</th>
                    <th scope="col" className="w-28 pb-2 text-right font-medium">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {rankRows.map((r, i) => (
                    <tr key={r.name + i} className="border-b border-line/60 last:border-0">
                      <td className="py-2.5">
                        <span className={cn('grid size-6 place-items-center rounded-md text-[11px] font-bold tabular-nums',
                          RANK_MEDAL[i] ?? 'bg-surface-2 text-muted')}>{i + 1}</span>
                      </td>
                      <td className="min-w-0 py-2.5 pr-2">
                        <span className="flex items-center gap-2">
                          {/* An identity chip: initials for a person or a territory, the product mark
                              for a SKU. Tinted from the NAME so the colour is that row's, and stays
                              that row's however the ranking reshuffles. */}
                          {/* The real product photograph when the catalogue has one. A named thing
                              is recognised by its picture faster than by its label, and the tinted
                              initials/box chip stays as the fallback so the column never collapses
                              to a ragged mix of images and blanks. */}
                          {r.img ? (
                            <img
                              src={fileUrl(r.img)} alt=""
                              className="size-7 shrink-0 rounded-lg border border-line bg-surface-2 object-cover"
                              loading="lazy"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span className={cn('grid size-7 shrink-0 place-items-center rounded-lg text-[10px] font-bold [&>svg]:size-3.5',
                              identityTint(r.name))} aria-hidden>
                              {rankTab === 'products' ? <Package /> : initials(r.name)}
                            </span>
                          )}
                          <span className="min-w-0">
                            {r.link ? (
                              <button type="button" onClick={() => go(r.link as string)}
                                className="block max-w-full truncate text-left font-medium text-content underline-offset-2 hover:text-brand-600 hover:underline">
                                {r.name}
                              </button>
                            ) : <span className="block truncate font-medium text-content">{r.name}</span>}
                            {r.sub ? <span className="block truncate text-[11px] text-subtle">{r.sub}</span> : null}
                          </span>
                        </span>
                      </td>
                      <td className="hidden py-2.5 pr-3 sm:table-cell">
                        <span className="flex items-center gap-2">
                          <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-3" aria-hidden>
                            <span className="block h-full rounded-full bg-brand-500 transition-[width]"
                              style={{ width: Math.max(2, (r.value / rankTotal) * 100) + '%' }} />
                          </span>
                          <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-muted">
                            {Math.round((r.value / rankTotal) * 100)}%
                          </span>
                        </span>
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-muted">{num(r.qty)}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-content">{inrShort(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <Empty
                title="Nothing has been billed in this period"
                detail={'Rankings are built from invoiced revenue, and none of the ' + num(live) + ' live orders has reached an invoice yet.'}
                action={live > 0 ? <Button size="sm" className="mt-2" onClick={() => go('/orders?' + periodQs({ salesPersonId }))}>See the orders</Button> : undefined}
              />
            )}
          </Panel>
        )}

        {canActivity && (
          <Panel className="xl:col-span-4" icon={<Activity />} title="Recent activity"
            action={<PanelLink label="View all" onClick={() => go('/admin/activity')} />}>
            {activity.length ? (
              /* A TIMELINE, not a log dump.
                 Straight from the activity table these rows are raw audit prose — one of them ran to
                 three wrapped lines ("…HDFC statement for 07 Sep shows no ₹10,000 credit. Asmita
                 still holds these notes.. The amount is back with the depositor.") — and six of those
                 turn a feed into a wall. Each entry is now a headline clamped to one line, a quiet
                 second line naming who and what, and a connector rail down the left so the eye reads
                 it as a sequence of events rather than a paragraph. */
              <ol className="relative space-y-3.5 before:absolute before:bottom-3 before:left-[13px] before:top-3 before:w-px before:bg-line">
                {activity.map((a: any) => {
                  const meta = ACTIVITY_ICON[a.module] ?? { icon: <Activity />, tone: 'bg-surface-2 text-muted' };
                  const href = activityHref(a);
                  const body = (
                    <>
                      <span className={cn('z-10 mt-0.5 grid size-[27px] shrink-0 place-items-center rounded-full ring-4 ring-surface [&>svg]:size-3.5', meta.tone)} aria-hidden>
                        {meta.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2">
                          <span className={cn('line-clamp-1 min-w-0 flex-1 text-sm text-content', href && 'group-hover/act:text-brand-600 dark:group-hover/act:text-brand-400')}>
                            {a.message || `${a.module} ${a.action}`.replace(/_/g, ' ').toLowerCase()}
                          </span>
                          <span className="shrink-0 text-[11px] text-subtle">{ago(a.createdAt)}</span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-subtle">
                          {a.actor?.name ?? 'System'}
                          {a.referenceLabel ? ` · ${a.referenceLabel}` : ''}
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li key={a.id} className="relative">
                      {href ? (
                        <button
                          type="button" onClick={() => go(href)}
                          className="group/act -mx-1.5 flex w-[calc(100%+0.75rem)] gap-2.5 rounded-control px-1.5 py-1 text-left transition-colors hover:bg-surface-2"
                        >
                          {body}
                        </button>
                      ) : (
                        <span className="flex gap-2.5 px-1.5 py-1">{body}</span>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : <Empty title={activityQ.isLoading ? 'Loading…' : 'No recent activity'} />}
          </Panel>
        )}

        {/* Quick actions. Not decoration: the four things a reader most often leaves this page to go
            and do, so the dashboard ends in a next step rather than in a dead end. */}
        <section className="flex min-w-0 flex-col overflow-hidden rounded-card border border-line bg-surface shadow-e1 xl:col-span-3">
          <div className="relative overflow-hidden border-b border-line bg-brand-50/60 px-4 py-3.5 dark:bg-brand-900/20">
            <div className="pointer-events-none absolute -right-4 -top-6 size-24 rounded-full bg-brand-500/10 blur-2xl" />
            <p className="relative flex items-center gap-2 text-sm font-semibold text-content">
              <Rocket className="size-4 text-brand-600" /> Keep your business moving
            </p>
            <p className="relative mt-0.5 text-xs text-muted">More insights. Better decisions.</p>
          </div>
          {/* Two-up tiles, each in its own subject colour. As a grey single-column list these read as
              menu rows — the same weight as everything else on the page — and a shortcut nobody
              notices is not a shortcut. The tint is the destination's colour elsewhere in the app
              (orders green, complaints red, stock blue, people violet), so the panel teaches the same
              colour language the KPI row uses. */}
          <div className="grid grid-cols-2 gap-2 p-3">
            {[
              { icon: <FileText />, label: 'Create order', sub: 'New sales order', to: '/orders/new', tint: 'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300', wash: 'border-brand-500/25 from-brand-500/[0.10]', show: true },
              { icon: <Wrench />, label: 'Complaints', sub: 'Customer issues', to: '/complaints', tint: 'bg-danger-500/10 text-danger-600 dark:bg-danger-500/20 dark:text-danger-400', wash: 'border-danger-500/25 from-danger-500/[0.09]', show: true },
              { icon: <Boxes />, label: 'Stock report', sub: 'Across godowns', to: '/inventory/stock', tint: 'bg-sky-400/15 text-sky-600 dark:bg-sky-400/20 dark:text-sky-300', wash: 'border-sky-500/25 from-sky-500/[0.10]', show: canExec },
              { icon: <Users />, label: 'Field force', sub: 'Track your team', to: '/ffa/attendance', tint: 'bg-plum-400/15 text-plum-600 dark:bg-plum-400/20 dark:text-plum-300', wash: 'border-plum-400/25 from-plum-400/[0.10]', show: canExec },
            ].filter((a) => a.show).map((a) => (
              // The whole tile now carries the destination's colour, not just its icon chip: a bare
              // bordered rectangle with one coloured square in the corner still read as a menu row.
              // Raised at rest, raised further on hover — never moved.
              <button key={a.to} type="button" onClick={() => go(a.to)}
                className={cn('group flex flex-col gap-2 rounded-card border bg-surface bg-linear-to-b to-80% to-transparent px-3 py-3 text-left shadow-e1 transition-shadow hover:shadow-e2', a.wash)}>
                <span className={cn('grid size-9 place-items-center rounded-lg [&>svg]:size-4', a.tint)} aria-hidden>{a.icon}</span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-content">{a.label}</span>
                  <span className="block truncate text-[11px] text-subtle">{a.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
